import angular from 'angular';

import r2a from '@web/angularIntegration/r2a';
import { App } from '@web/App/App';
import { notificationFactory } from '@web/common/utils/browserAPI/notification';

import config from '@angular/config';
import wavPath from '@angular/assets/sounds/ReceivedMessage.wav';

import cdnResizeUrl from '../components/services/cdnResizeUrl';
import _isCdnResizeEnabled from '../components/services/isCdnResizeEnabled';

import memberRowComponent from './components/memberRow';
import membersGroupRowComponent from './components/membersGroupRow';
import introductionUrl from './introduction.html';

const messagesModule = angular.module('sling.messages', []);
export default messagesModule.name;

messagesModule.config(($stateProvider) => {
  $stateProvider.state('messages', {
    component: 'rApp',
    data: {
      title: 'Messages',
    },
    url: '/messages/{path:any}',
    params: {
      path: {
        dynamic: true,
        value: null,
        raw: true,
        squash: true,
      },
    },
    onEnter($uibModal, $state, Authentication) {
      const { adminOnboardingCompleted, messagesHelpShown, onboardingComplete } =
        Authentication.user.settings;

      if (
        !Authentication.isOrgFeatureEnabled('privateMessages') &&
        !Authentication.isOrgFeatureEnabled('groupMessages')
      ) {
        return;
      }

      if (
        !$state.params.new &&
        !messagesHelpShown &&
        (onboardingComplete || adminOnboardingCompleted)
      ) {
        $uibModal
          .open({
            windowClass: 'sling-tour-modal sling-animate',
            templateUrl: introductionUrl,
          })
          .result.then(null, () => {
            Authentication.user.settings.messagesHelpShown = true;
            Authentication.syncSettings();
          });
      }
    },
    resolve: {
      premiumGuardBridge: (AccountService) => ({
        openPremiumGuardModal: ({ premiumFeatureName, stateAfter, premiumFeature }) =>
          AccountService.openPremiumGuardModal(premiumFeatureName, stateAfter, premiumFeature),
        openPricingModal: () => AccountService.openPricingModal(),
        openLaborCostVideoModal: (_) => _,
      }),
      socketConnection: ['$rootScope', ($rootScope) => $rootScope.Socket],
      statusService: (StatusService) => StatusService,
    },
  });
});

messagesModule.service(
  'MessageNotification',
  function MessageNotification(
    $rootScope,
    $window,
    Authentication,
    PersonaCollection,
    StatusService
  ) {
    'ngInject';

    const service = this;

    function shouldNotify(message) {
      const messagesNotify = Authentication.user.settings.messagesNotify;

      // disabled
      if (messagesNotify === 0) {
        return false;
      }

      // always notify
      if (messagesNotify === 1) {
        return true;
      }

      // only when mentioned or in private conversation
      if (messagesNotify === 2 && message.private) {
        return true;
      }

      // at this point any mentions are expected
      if (message.content.indexOf('@') === -1) {
        return false;
      }

      // check if any metioned persona applies to the current user
      const regexTags = [
        personaToRegex(Authentication.user),
        ...PersonaCollection.me.groupIds.map((groupId) =>
          personaToRegex(PersonaCollection.personasDict[groupId])
        ),
      ];

      return regexTags.some((regexTag) => new RegExp(regexTag).test(message.content));
    }

    $rootScope.$on('socket:message:new', (event, data) => {
      const isMessageNotificationAppilcable =
        data.type === 'message' && data.author.id !== Authentication.user.id && !data.silenced;
      const messageBrowserNotificationEnabled = !!Authentication.user.clientsettings.notifications;
      const isWindowFocused = StatusService.focus;

      if (isMessageNotificationAppilcable && Authentication.user.settings.playSound) {
        const sound = new Audio(wavPath);
        sound.play();
      }

      if (
        !isMessageNotificationAppilcable ||
        !messageBrowserNotificationEnabled ||
        isWindowFocused
      ) {
        return;
      }

      if (shouldNotify(data)) {
        // format mention tags to human readible form
        const content = data.content.replace(/@([^@]+)\[(\d+)\]/g, (m, match1) => `@${match1}`);

        notificationFactory(`Message from ${data.author.name}`, {
          body: content || '[media]',
          closeTimeout: 3000,
          icon: getNotificationIconUrl(data),
          onClick: () => {
            $window.focus();
          },
        });
      }
    });

    return service;
  }
);

messagesModule.component(
  'rApp',
  r2a(App, ['premiumGuardBridge', 'socketConnection', 'statusService'])
);

// @TODO those are probably used on newsfeed module only,
// consider moving these components to newsfeed module
messagesModule.component('memberRow', memberRowComponent);
messagesModule.component('membersGroupRow', membersGroupRowComponent);

// exported for unit tests (SLING-979)
export function getNotificationIconUrl(message) {
  if (!message.attachments?.length) {
    return '/assets/icons/apple-icon.png';
  }

  const url = message.attachments[0].url;

  if (_isCdnResizeEnabled()) {
    const resized = cdnResizeUrl(url, 64, 64, 'cover');
    if (resized !== url) {
      return resized;
    }
  }

  return `${config.CLOUDINARY_BASE_URL}image/fetch/f_jpg,c_fill,h_64,w_64,b_rgb:ffffff/${encodeURIComponent(url)}`;
}

function personaToRegex(persona) {
  return `@(.*)\\[${persona.id}\\]`;
}
